import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { ShoppingCart } from "lucide-react";
import { usePosSession } from "../pos/hooks/usePosSession";
import { BarcodeInputHandler } from "../pos/barcode/BarcodeInputHandler";
import { BarcodeScanner } from "../pos/barcode/BarcodeScanner";
import { QuickAddProductModal } from "../pos/barcode/QuickAddProductModal";
import { TRACKING_EVENTS } from "../../constants/trackingEvents";
import { trackEvent } from "../../lib/tracking";
import { queryClient } from "../../app/queryClient";
import { formatInrTrimmed } from "../../lib/currency";
import { useBreakpoint } from "@/lib/useBreakpoint";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "../../app/components/ui/drawer";
import { Button } from "../../app/components/ui/button";
import { PosLayout } from "./layouts/PosLayout";
import { PosTopBar } from "./components/PosTopBar";
import { ProductPanel } from "./components/ProductPanel";
import { CartPanelV2 } from "./components/CartPanelV2";
import { AiHintsDock, type AiPosHint } from "./components/AiHintsDock";

export function PosV2Page() {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [dismissedHintIds, setDismissedHintIds] = useState<string[]>([]);
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";
  const isTablet = breakpoint === "tablet";
  const useDrawerCart = isMobile || isTablet;
  const navigate = useNavigate();

  const s = usePosSession({
    searchInputRef,
    onCloseMobileCart: () => setCartDrawerOpen(false),
  });

  useEffect(() => {
    if (!useDrawerCart) setCartDrawerOpen(false);
  }, [useDrawerCart]);

  const aiHints = useMemo((): AiPosHint[] => {
    const out: AiPosHint[] = [];
    for (const p of s.filteredProducts.slice(0, 80)) {
      const pr = s.cardPricingByProductId[String(p.id)];
      if (pr && pr.available_qty > 0 && pr.available_qty < 5) {
        out.push({
          id: `low-${p.id}`,
          type: "warning",
          message: `Low stock: ${p.name} (${s.formatQty(pr.available_qty)} left)`,
        });
        if (out.length >= 2) break;
      }
    }
    return out;
  }, [s.filteredProducts, s.cardPricingByProductId, s.formatQty]);

  useEffect(() => {
    setDismissedHintIds((prev) => prev.filter((id) => aiHints.some((hint) => hint.id === id)));
  }, [aiHints]);

  const visibleHints = useMemo(
    () => aiHints.filter((hint) => !dismissedHintIds.includes(hint.id)),
    [aiHints, dismissedHintIds],
  );

  const grand = s.quote ? Number.parseFloat(s.quote.totals.grand_total) : s.totals.grand;

  const cartPanelEl = (
    <CartPanelV2
      cart={s.cart}
      cartLines={s.cartLines}
      expandedCartPid={s.expandedCartPid}
      setExpandedCartPid={s.setExpandedCartPid}
      setCart={s.setCart}
      customerPhone={s.customerPhone}
      setCustomerPhone={s.setCustomerPhone}
      customerName={s.customerName}
      setCustomerName={s.setCustomerName}
      promoCode={s.promoCode}
      setPromoCode={s.setPromoCode}
      paymentMethod={s.paymentMethod}
      onPaymentMethodChange={s.onPaymentMethodChange}
      quote={s.quote}
      totals={s.totals}
      showComplianceWarning={s.showComplianceWarning}
      setItemQty={s.setItemQty}
      onCheckout={async () => {
        await s.onCheckout();
        setCartDrawerOpen(false);
      }}
      checkoutLoading={s.checkoutLoading}
    />
  );

  return (
    <>
      {s.resolving ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/60 backdrop-blur-sm">
          <div className="rounded-lg border bg-card px-6 py-4 shadow-lg text-sm text-muted-foreground">
            Fetching product details...
          </div>
        </div>
      ) : null}

      <QuickAddProductModal
        open={s.scanModalOpen}
        onOpenChange={s.setScanModalOpen}
        barcode={s.scanBarcode}
        mode={s.scanModalMode}
        externalPrefill={s.scanExternalPrefill}
        brands={s.brands}
        categories={s.categories}
        dealers={s.dealers}
        onSuccess={async ({ id, name }) => {
          queryClient.removeQueries({ queryKey: ["posPricing", id] });
          await s.reloadProducts();
          const ok = await s.addProductToCart(id, name);
          if (ok) toast.success("Product added");
        }}
      />

      <BarcodeInputHandler
        enabled={s.scannerMode === "usb"}
        onBarcode={(b) => {
          trackEvent(TRACKING_EVENTS.BARCODE_SCANNED, { type: "usb" });
          void s.resolveBarcode(b);
        }}
      />
      <BarcodeScanner
        open={s.cameraOpen}
        onClose={() => s.setCameraOpen(false)}
        onDetected={(b) => {
          trackEvent(TRACKING_EVENTS.BARCODE_SCANNED, { type: "camera" });
          void s.resolveBarcode(b);
        }}
      />

      <div className="flex h-full min-h-0 flex-col">
        <PosLayout
          omitTabletStackCart={useDrawerCart}
          topBar={
            <PosTopBar
              ref={searchInputRef}
              query={s.query}
              onQueryChange={s.setQuery}
              scannerMode={s.scannerMode}
              onOpenCameraScan={() => s.setCameraOpen(true)}
              onNewSale={() => {
                s.newSale();
                toast.message("Started a new sale");
              }}
              onHoldBill={() => toast.message("Hold bill is not available yet")}
              onRecentSales={() => navigate("/sales")}
            />
          }
          productPanel={
            <ProductPanel
              filteredProducts={s.filteredProducts}
              categories={s.categories}
              cardPricingByProductId={s.cardPricingByProductId}
              productView={s.productView}
              onProductViewChange={s.setProductView}
              formatQty={s.formatQty}
              onAddProduct={async (id, name) => {
                const ok = await s.addProductToCart(id, name);
                if (ok) {
                  toast.success("Product added");
                  if (useDrawerCart) setCartDrawerOpen(true);
                }
              }}
              productsLoading={s.productsLoading}
              productsError={s.productsError}
            />
          }
          cartPanel={useDrawerCart ? null : cartPanelEl}
        />

        {useDrawerCart ? (
          <>
            <Drawer open={cartDrawerOpen} onOpenChange={setCartDrawerOpen} shouldScaleBackground={false}>
              <DrawerContent className="flex max-h-[92vh] flex-col gap-0 p-0">
                <DrawerHeader className="sr-only">
                  <DrawerTitle>Current sale</DrawerTitle>
                  <DrawerDescription>Cart and checkout</DrawerDescription>
                </DrawerHeader>
                <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">{cartPanelEl}</div>
              </DrawerContent>
            </Drawer>
            <Button
              type="button"
              variant="default"
              className={`fixed z-[45] flex h-12 min-h-11 items-center gap-2 rounded-full px-5 shadow-lg [@media(pointer:coarse)]:min-h-12 ${
                isMobile ? "left-1/2 -translate-x-1/2" : "right-5"
              }`}
              style={{
                bottom: isMobile
                  ? "calc(4.25rem + env(safe-area-inset-bottom, 0px))"
                  : "calc(1rem + env(safe-area-inset-bottom, 0px))",
              }}
              onClick={() => setCartDrawerOpen(true)}
              aria-label={`Open cart, ${s.cart.length} items, total ${formatInrTrimmed(grand)}`}
            >
              <ShoppingCart className="h-5 w-5 shrink-0" aria-hidden />
              <span className="text-sm font-semibold">
                {s.cart.length} · {formatInrTrimmed(grand)}
              </span>
            </Button>
          </>
        ) : null}

        <AiHintsDock
          hints={visibleHints}
          onDismissHint={(id) => setDismissedHintIds((prev) => [...prev, id])}
          onDismissAll={() => setDismissedHintIds(aiHints.map((hint) => hint.id))}
        />
      </div>
    </>
  );
}
